import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Activity, Lock, Loader2, AlertTriangle } from "lucide-react";

export const isMarrowCase = (c = {}) =>
  `${c.specimen_type || ""} ${c.specimen_site || ""} ${c.procedure || ""}`
    .toLowerCase().includes("marrow");

const FLAG_STYLE = {
  "critical-low": "bg-red-800 text-white",
  "critical-high": "bg-red-800 text-white",
  low: "bg-sky-100 text-sky-900",
  high: "bg-amber-100 text-amber-900",
  normal: "",
};
const FLAG_LABEL = {
  "critical-low": "Critical low", "critical-high": "Critical high", low: "Low", high: "High",
};

const Row = ({ r, testid }) => (
  <tr className="border-b border-slate-100 last:border-0 dark:border-slate-700" data-testid={testid}>
    <td className="py-1.5 pr-2 text-slate-700 dark:text-slate-200">{r.name}</td>
    <td className="py-1.5 pr-2 text-right">
      <span className={`num rounded px-1.5 py-0.5 font-semibold ${FLAG_STYLE[r.flag] || "text-slate-900 dark:text-slate-100"}`}>
        {r.value}
      </span>
    </td>
    <td className="py-1.5 pr-2 text-xs text-slate-500">{r.unit}</td>
    <td className="py-1.5 pr-2 text-right text-xs text-slate-500">{r.reference_range}</td>
    <td className="py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
      {FLAG_LABEL[r.flag] || ""}
    </td>
  </tr>
);

const Table = ({ title, rows, prefix }) => (
  <div>
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
    <table className="w-full text-sm" data-testid={`lablync-table-${prefix}`}>
      <tbody>
        {rows.map((r) => (
          <Row key={r.name} r={r} testid={`lablync-${prefix}-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
        ))}
      </tbody>
    </table>
  </div>
);

/** Read-only CBC and differential from the hospital feed, shown on bone marrow cases. */
export const LabLyncPanel = ({ caseId, enabled = true }) => {
  const { can } = useAuth();
  const allowed = enabled && can("compliance:view");
  const [panel, setPanel] = useState(null);
  const [state, setState] = useState(allowed ? "loading" : "none");

  useEffect(() => {
    if (!allowed) { setState("none"); return undefined; }
    let live = true;
    setState("loading");
    api.get(`/lablync/cases/${caseId}`)
      .then(({ data }) => { if (live) { setPanel(data); setState("ready"); } })
      .catch(() => live && setState("none"));
    return () => { live = false; };
  }, [caseId, allowed]);

  if (state === "loading") {
    return <Loader2 className="animate-spin text-slate-400" size={18} data-testid="lablync-loading" />;
  }
  if (state === "none" || !panel) return null;

  return (
    <section className="border border-slate-200 dark:border-slate-700" data-testid="lablync-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
        <Activity size={15} className="text-[#134e4a]" />
        <h3 className="text-sm font-semibold">Hematology for this patient</h3>
        <span className="text-xs text-slate-500">LabLync</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              data-testid="lablync-readonly-pill">
          <Lock size={10} /> Read-only
        </span>
        {panel.critical_names.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-800 px-2 py-0.5 text-[11px] font-semibold text-white"
                data-testid="lablync-critical-pill">
            <AlertTriangle size={10} /> {panel.critical_names.length} critical
          </span>
        )}
        <span className="ml-auto text-xs text-slate-500" data-testid="lablync-collected">
          Collected {new Date(panel.collected_at).toLocaleString()}
        </span>
      </div>

      <div className="grid gap-5 px-3 py-3 lg:grid-cols-2">
        <Table title="Complete blood count" rows={panel.cbc} prefix="cbc" />
        <Table title="Differential" rows={panel.differential} prefix="diff" />
      </div>

      <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-700"
         data-testid="lablync-source">
        {panel.source_system} · order {panel.order_number} · {panel.performed_by}. Results are
        displayed for review only and cannot be edited in PathQity.
      </p>
    </section>
  );
};
